import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { fmtMoney } from "@/lib/finance";

export const PageHeader = ({ title, subtitle, actions, testid }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-6" data-testid={testid}>
    <div>
      <h1 className="font-serif-display text-3xl sm:text-4xl tracking-[-0.02em] text-[var(--text)]">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-[var(--text-muted)] max-w-2xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
  </div>
);

export const SectionCard = ({ title, subtitle, right, children, className, contentClass, testid }) => (
  <Card
    data-testid={testid}
    className={cn(
      "rounded-[var(--radius-card)] bg-[var(--surface-1)] border border-[color:var(--border-subtle)] shadow-[var(--shadow-sm)] transition-shadow hover:shadow-[var(--shadow-md)]",
      className
    )}
  >
    {(title || right) && (
      <div className="flex items-start justify-between gap-3 p-5 pb-3">
        <div>
          {title && <h3 className="font-serif-display text-lg text-[var(--text)]">{title}</h3>}
          {subtitle && <p className="text-xs text-[var(--text-faint)] mt-0.5">{subtitle}</p>}
        </div>
        {right}
      </div>
    )}
    <div className={cn("px-5 pb-5", !title && "pt-5", contentClass)}>{children}</div>
  </Card>
);

export const KpiCard = ({ label, value, sub, accent, icon: Icon, testid }) => (
  <Card
    data-testid={testid}
    className={cn(
      "rounded-[var(--radius-card)] bg-[var(--surface-1)] border border-[color:var(--border-subtle)] shadow-[var(--shadow-sm)] p-5 transition-shadow hover:shadow-[var(--shadow-md)]",
      accent && "border-[color:rgba(198,139,60,0.35)]"
    )}
  >
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-[0.12em] text-[var(--text-muted)]">{label}</span>
      {Icon && (
        <span className={cn("grid place-items-center h-8 w-8 rounded-full",
          accent ? "bg-[color:rgba(198,139,60,0.14)] text-[var(--gold)]" : "bg-[color:rgba(31,115,85,0.10)] text-[var(--green)]")}>
          <Icon className="h-4 w-4" />
        </span>
      )}
    </div>
    <div className="mt-3 font-serif-display text-3xl tracking-[-0.01em] tabular-nums text-[var(--text)]">{value}</div>
    {sub && <div className="mt-1 text-xs text-[var(--text-muted)]">{sub}</div>}
  </Card>
);

export const Money = ({ value, className, decimals }) => {
  const negative = Number(value) < 0;
  return (
    <span className={cn("tabular-nums", negative ? "text-[var(--danger)]" : "", className)}>
      {fmtMoney(value, { decimals })}
    </span>
  );
};

export const EmptyState = ({ icon: Icon, title, message, action, testid }) => (
  <div className="flex flex-col items-center justify-center text-center py-12 px-6" data-testid={testid}>
    {Icon && (
      <span className="grid place-items-center h-12 w-12 rounded-full bg-[color:rgba(31,115,85,0.10)] text-[var(--green)] mb-4">
        <Icon className="h-6 w-6" />
      </span>
    )}
    <p className="font-serif-display text-lg text-[var(--text)]">{title}</p>
    {message && <p className="text-sm text-[var(--text-muted)] mt-1 max-w-sm">{message}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const SeverityBadge = ({ type, severity, children }) => {
  const map = {
    strength: "bg-[color:rgba(31,115,85,0.12)] text-[var(--green)] border-[color:rgba(31,115,85,0.20)]",
    low: "border-[color:rgba(198,139,60,0.30)] text-[color:rgba(23,35,31,0.85)]",
    med: "border-[color:rgba(198,139,60,0.40)] text-[color:rgba(23,35,31,0.9)]",
    high: "border-[color:rgba(180,35,24,0.30)] text-[#7a1b12]",
  };
  const bg = { low: "var(--risk-low)", med: "var(--risk-med)", high: "var(--risk-high)" };
  const isStrength = type === "strength";
  return (
    <span
      data-testid="severity-badge"
      className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium capitalize",
        isStrength ? map.strength : map[severity])}
      style={isStrength ? {} : { backgroundColor: bg[severity] }}
    >
      {children || (isStrength ? "Strength" : `${severity} risk`)}
    </span>
  );
};

export const StatusPill = ({ status }) => {
  const map = {
    active: "bg-[color:rgba(31,115,85,0.12)] text-[var(--green)]",
    completed: "bg-[color:rgba(31,115,85,0.18)] text-[var(--green)]",
    paused: "bg-[color:rgba(23,35,31,0.08)] text-[var(--text-muted)]",
    protected: "bg-[color:rgba(31,115,85,0.12)] text-[var(--green)]",
    review: "bg-[color:rgba(198,139,60,0.20)] text-[#8a5a17]",
    gap: "bg-[color:rgba(180,35,24,0.14)] text-[#7a1b12]",
    high: "bg-[color:rgba(180,35,24,0.12)] text-[#7a1b12]",
    med: "bg-[color:rgba(198,139,60,0.18)] text-[#8a5a17]",
    low: "bg-[color:rgba(23,35,31,0.08)] text-[var(--text-muted)]",
  };
  return (
    <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium capitalize", map[status] || map.low)}>
      {status}
    </span>
  );
};

export const AddButton = ({ children, onClick, testid, variant }) => (
  <Button
    data-testid={testid}
    onClick={onClick}
    className={cn(
      variant === "secondary"
        ? "bg-[var(--surface-2)] text-[var(--text)] border border-[color:var(--border-subtle)] hover:bg-[color:rgba(244,241,232,0.6)] hover:border-[color:var(--border-strong)]"
        : "bg-[var(--green)] text-[#f7fbf9] hover:bg-[color:rgba(31,115,85,0.9)]"
    )}
  >
    {children}
  </Button>
);

export const ProgressBar = ({ value, max, tone = "green" }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const color = tone === "gold" ? "var(--gold)" : tone === "danger" ? "var(--danger)" : "var(--green)";
  return (
    <div className="h-2 w-full rounded-full bg-[color:rgba(23,35,31,0.08)] overflow-hidden">
      <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
};
